import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Card, CardBody, Row, Col } from "reactstrap";
import moment from "moment";
import { getPmSessionMonthRangeStrings } from "helpers/pmSessionDateHelper";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import "./NonAdherentBackToBackMonths.css";
import "../PatientCPTRequirements/ReportComponents.css";
import NonAdherentFilters from "./NonAdherentFilters";
import NonAdherentReportShimmer from "./NonAdherentReportShimmer";
import CptDashboardSkeletonOverlay from "../common/CptDashboardSkeletonOverlay";
import {
  getNonAdherentPatientListColumns,
  processNonAdherentPatientData
} from "./NonAdherentPatientListTable";
import NonAdherentPatientTimelineReport from "./NonAdherentPatientTimelineReport";
import { getCptAnalyticsTimezone } from "../common/cptAnalyticsTimezone";

const { ExportCSVButton } = CSVExport;
const DEFAULT_PAGE_SIZE = 5;

const normalizePatientStatusFilter = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/_/g, "-");
  if (normalized === "adherent") return "adherent";
  if (normalized === "non-adherent") return "non-adherent";
  return "all";
};

const buildResolvedDateRange = (range = {}, fallbackMonth, fallbackYear) => {
  const resolvedMonth = String(
    range?.selectedMonth
      || (moment(range?.startDate, "YYYY-MM-DD", true).isValid() ? moment(range.startDate).format("MM") : "")
      || fallbackMonth
      || moment().format("MM")
  ).padStart(2, "0");
  const resolvedYear = String(
    range?.selectedYear
      || (moment(range?.startDate, "YYYY-MM-DD", true).isValid() ? moment(range.startDate).format("YYYY") : "")
      || fallbackYear
      || moment().format("YYYY")
  );
  const { startDate, endDate } = getPmSessionMonthRangeStrings(resolvedYear, resolvedMonth);

  return {
    startDate,
    endDate,
    selectedMonth: resolvedMonth,
    selectedYear: resolvedYear
  };
};

const getPatientDashboardPath = (patient = {}) => {
  const patientId = patient?.patient_id || patient?._id || patient?.id || patient?.patientId || patient?.userId;
  if (!patientId) return "";

  const fullName = String(patient?.patient_name || patient?.patientName || "").trim();
  const [lastName = "", firstName = ""] = fullName.split(",").map((value) => String(value || "").trim());
  const sanitize = (value) =>
    String(value || "")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      .toLowerCase();

  const slug = `${sanitize(firstName)}-${sanitize(lastName)}`.replace(/^-|-$/g, "");
  return `/dashboard/${slug || "patient"}/${patientId}`;
};

const NonAdherentBackToBackMonths = ({
  dispatch,
  nonAdherentBackToBackReportData,
  ongoingNonAdherentBackToBackReportRequest,
  nonAdherentBackToBackTimelineData,
  ongoingNonAdherentBackToBackTimelineRequest
}) => {
  const currentMonth = moment().format("MM");
  const currentYear = moment().format("YYYY");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [dateRange, setDateRange] = useState(() => {
    const { startDate, endDate } = getPmSessionMonthRangeStrings(currentYear, currentMonth);
    return {
      startDate,
      endDate,
      selectedMonth: currentMonth,
      selectedYear: currentYear
    };
  });
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientStatusFilter, setPatientStatusFilter] = useState("all");
  const [careProviderFilter, setCareProviderFilter] = useState("all");
  const [clinicFilter, setClinicFilter] = useState("all");
  const timelineSectionRef = useRef(null);

  const scrollToTimelineSection = () => {
    if (!timelineSectionRef.current) return;
    timelineSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    dispatch({ type: "RESET_NON_ADHERENT_BACK_TO_BACK_REPORT" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = (override = {}) => {
    const nextDateRange = buildResolvedDateRange(
      override.dateRange || dateRange,
      selectedMonth,
      selectedYear
    );
    const nextPatientStatusFilter = normalizePatientStatusFilter(
      override.patientStatusFilter !== undefined ? override.patientStatusFilter : patientStatusFilter
    );
    const payload = {
      startDate: nextDateRange.startDate,
      endDate: nextDateRange.endDate,
      filter: nextPatientStatusFilter,
      patientStatusFilter: nextPatientStatusFilter,
      timezone: getCptAnalyticsTimezone()
    };
    dispatch({ type: "DO_GET_NON_ADHERENT_BACK_TO_BACK_REPORT", payload });
  };

  const fetchTimeline = (patientId, override = {}) => {
    if (!patientId) return;
    const nextDateRange = buildResolvedDateRange(
      override.dateRange || dateRange,
      selectedMonth,
      selectedYear
    );
    dispatch({ type: "RESET_NON_ADHERENT_BACK_TO_BACK_TIMELINE" });
    const payload = {
      patient_id: patientId,
      startDate: nextDateRange.startDate,
      endDate: nextDateRange.endDate,
      timezone: getCptAnalyticsTimezone()
    };
    dispatch({ type: "DO_GET_NON_ADHERENT_BACK_TO_BACK_TIMELINE", payload });
  };

  const reportRootData = nonAdherentBackToBackReportData || {};
  const reportData = reportRootData?.data || reportRootData;
  const summary = reportData?.summary || {};
  const patients = Array.isArray(reportData?.patients) ? reportData.patients : [];
  const mappedRows = useMemo(() => (
    patients.map((patient) => {
        const technical = patient?.component_status?.technical || {};
        const professional = patient?.component_status?.professional || {};
        const mappedCodes = {};
        const addComponentCodes = (componentKey, label, componentData) => {
          const allCodes = Array.isArray(componentData?.all_cpt_codes)
            ? componentData.all_cpt_codes
            : [];
          const metCodes = new Set(
            Array.isArray(componentData?.met_cpt_codes)
              ? componentData.met_cpt_codes.map((code) => String(code))
              : []
          );
          const notMetCodes = new Set(
            Array.isArray(componentData?.not_met_cpt_codes)
              ? componentData.not_met_cpt_codes.map((code) => String(code))
              : []
          );

          const fallbackCodes = Array.isArray(componentData?.cpt_codes) ? componentData.cpt_codes : [];
          const sourceCodes = allCodes.length ? allCodes : fallbackCodes;

          if (!sourceCodes.length) {
            mappedCodes[`${componentKey}-na`] = {
              code: "-",
              met: Boolean(componentData?.present),
              name: `${label} (${Number(componentData?.count || 0)})`,
              componentType: componentKey,
              componentLabel: label,
              componentMet: Boolean(componentData?.present),
              componentCount: Number(componentData?.count || 0)
            };
            return;
          }

          sourceCodes.forEach((rawCode, index) => {
            const codeValue = String(rawCode || "-");
            const codeIsMet = metCodes.has(codeValue) ? true : (notMetCodes.has(codeValue) ? false : Boolean(componentData?.present));
            mappedCodes[`${componentKey}-${codeValue}-${index}`] = {
              code: codeValue,
              met: codeIsMet,
              name: `${label} (${Number(componentData?.count || 0)})`,
              componentType: componentKey,
              componentLabel: label,
              componentMet: Boolean(componentData?.present),
              componentCount: Number(componentData?.count || 0)
            };
          });
        };

        addComponentCodes("technical", "Technical Component", technical);
        addComponentCodes("professional", "Professional Component", professional);

        const technicalAllCodes = Array.isArray(technical?.all_cpt_codes) ? technical.all_cpt_codes : [];
        const professionalAllCodes = Array.isArray(professional?.all_cpt_codes) ? professional.all_cpt_codes : [];

        return {
          ...patient,
          patientName: patient?.patientName || patient?.patient_name,
          status: patient?.status === "non_adherent" ? "notMet" : "met",
          mainCareProvider: {
            name: patient?.provider_name || patient?.mainCareProvider?.name || "-"
          },
          client: {
            organization: patient?.clinic_name || patient?.client?.organization || "-"
          },
          csv_patient_id: patient?.patient_id || patient?._id || "-",
          csv_patient_name: patient?.patient_name || patient?.patientName || "-",
          csv_date_of_birth: patient?.date_of_birth ? moment(patient.date_of_birth).format("MM/DD/YYYY") : "-",
          csv_provider_name: patient?.provider_name || patient?.mainCareProvider?.name || "-",
          csv_clinic_name: patient?.clinic_name || patient?.client?.organization || "-",
          csv_streak_length: patient?.streak?.length || 0,
          csv_status: String(patient?.status || "-").replace(/_/g, "-"),
          cptCodes: mappedCodes,
          additionalDetails: {
            technicalComponent: technical?.present ? "Present" : "Missing",
            technicalCodes: technicalAllCodes.length ? technicalAllCodes.join(", ") : "-",
            professionalComponent: professional?.present ? "Present" : "Missing",
            professionalCodes: professionalAllCodes.length ? professionalAllCodes.join(", ") : "-",
            streakLengthMonths: patient?.streak?.length || 0
          }
        };
      })
  ), [patients]);

  const careProviderOptions = useMemo(() => {
    const options = mappedRows
      .map((row) => String(row?.csv_provider_name || "").trim())
      .filter((value) => value && value !== "-")
      .reduce((acc, value) => {
        const key = value.toLowerCase();
        if (!acc.some((item) => item.value === key)) {
          acc.push({ value: key, label: value });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: "all", label: "All Providers" }, ...options];
  }, [mappedRows]);

  const clinicOptions = useMemo(() => {
    const options = mappedRows
      .map((row) => String(row?.csv_clinic_name || "").trim())
      .filter((value) => value && value !== "-")
      .reduce((acc, value) => {
        const key = value.toLowerCase();
        if (!acc.some((item) => item.value === key)) {
          acc.push({ value: key, label: value });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: "all", label: "All Clinics" }, ...options];
  }, [mappedRows]);

  const locallyFilteredRows = useMemo(() => {
    return mappedRows.filter((row) => {
      const providerName = String(row?.csv_provider_name || "").trim().toLowerCase();
      const clinicName = String(row?.csv_clinic_name || "").trim().toLowerCase();
      const isProviderMatch = careProviderFilter === "all" || providerName === careProviderFilter;
      const isClinicMatch = clinicFilter === "all" || clinicName === clinicFilter;
      return isProviderMatch && isClinicMatch;
    });
  }, [mappedRows, careProviderFilter, clinicFilter]);

  const filteredRows = useMemo(() => {
    const query = String(searchText || "").toLowerCase().trim();
    const rows = processNonAdherentPatientData(locallyFilteredRows);

    return rows.filter((patient) => {
      if (!query) return true;
      return [
        patient?.patientName,
        patient?.email,
        patient?.phone,
        patient?.mainCareProviderName,
        patient?.organization,
        patient?.statusLabel,
        patient?.additionalDetailsText
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [locallyFilteredRows, searchText]);

  const handleLocalFilterChange = ({ careProviderFilter: nextCareProviderFilter, clinicFilter: nextClinicFilter }) => {
    setCareProviderFilter(nextCareProviderFilter || "all");
    setClinicFilter(nextClinicFilter || "all");
    setPage(1);
  };

  const handleFilterApply = (filterState = {}) => {
    const resolvedFilterState = buildResolvedDateRange(
      filterState,
      selectedMonth,
      selectedYear
    );
    const normalizedPatientStatusFilter = normalizePatientStatusFilter(filterState.patientStatusFilter || patientStatusFilter);
    setPatientStatusFilter(normalizedPatientStatusFilter);
    setSelectedMonth(resolvedFilterState.selectedMonth);
    setSelectedYear(resolvedFilterState.selectedYear);
    setDateRange(resolvedFilterState);
    setPage(1);
    fetchReport({
      page: 1,
      dateRange: resolvedFilterState,
      patientStatusFilter: normalizedPatientStatusFilter
    });
  };

  const handleFilterReset = () => {
    const resetDateRange = buildResolvedDateRange({}, currentMonth, currentYear);
    setPatientStatusFilter("all");
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
    setDateRange(resetDateRange);
    setCareProviderFilter("all");
    setClinicFilter("all");
    setPage(1);
    setSelectedPatientId("");
    dispatch({ type: "RESET_NON_ADHERENT_BACK_TO_BACK_REPORT" });
    dispatch({ type: "RESET_NON_ADHERENT_BACK_TO_BACK_TIMELINE" });
  };

  const hasLocalResponseFilters = careProviderFilter !== "all" || clinicFilter !== "all";
  const totalRecords = hasLocalResponseFilters
    ? locallyFilteredRows.length
    : (reportData?.pagination?.total || reportData?.total || summary?.total_patients || filteredRows.length);
  const effectiveTotalRecords = searchText ? filteredRows.length : totalRecords;
  const timelineData = nonAdherentBackToBackTimelineData || {};
  const isReportLoading = ongoingNonAdherentBackToBackReportRequest;
  const isTimelineLoading = ongoingNonAdherentBackToBackTimelineRequest;
  const hasLoadedOnce = Boolean(nonAdherentBackToBackReportData);
  const showReportSkeleton = isReportLoading && !reportData?.patients;
  const totalPatients = hasLocalResponseFilters ? locallyFilteredRows.length : Number(summary?.total_patients || 0);
  const nonAdherentPatients = hasLocalResponseFilters
    ? locallyFilteredRows.filter((item) => String(item?.status || "").toLowerCase() === "notmet").length
    : Number(summary?.non_adherent_patients || 0);
  const adherentPatients = hasLocalResponseFilters
    ? Math.max(totalPatients - nonAdherentPatients, 0)
    : Number(summary?.adherent_patients !== undefined ? summary.adherent_patients : Math.max(totalPatients - nonAdherentPatients, 0));
  const adherentPercentage = totalPatients > 0 ? ((adherentPatients / totalPatients) * 100).toFixed(2) : "0.00";

  const columns = useMemo(() => {
    const baseColumns = getNonAdherentPatientListColumns(
      {
        onViewReport: (row) => {
          const patientId = row?.patient_id || row?._id;
          if (!patientId) return;
          setSelectedPatientId(patientId);
          fetchTimeline(patientId);
          window.requestAnimationFrame(() => {
            scrollToTimelineSection();
          });
        },
        onViewDetails: (row) => {
          const path = getPatientDashboardPath(row);
          if (!path) return;
          window.location.href = path;
        }
      }
    );

    const modalLikeColumns = baseColumns.filter((column) => column.dataField !== "statusLabel");
    const statusColumnIndex = modalLikeColumns.findIndex((column) => column.dataField === "patientActions");
    if (statusColumnIndex === -1) {
      return [...modalLikeColumns];
    }

    const reorderedColumns = [
      ...modalLikeColumns.slice(0, statusColumnIndex),
      ...modalLikeColumns.slice(statusColumnIndex)
    ];

    const uiColumns = reorderedColumns.map((column) => {
      if (["patientName", "technicalCptDetails", "professionalCptDetails", "patientActions"].includes(column.dataField)) {
        return { ...column, csvExport: false };
      }
      return column;
    });

    const csvColumns = [
      { dataField: "csv_patient_id", text: "Patient ID", csvExport: true, hidden: true },
      { dataField: "csv_patient_name", text: "Patient Name", csvExport: true, hidden: true },
      { dataField: "csv_date_of_birth", text: "Date of Birth", csvExport: true, hidden: true },
      { dataField: "csv_provider_name", text: "Provider", csvExport: true, hidden: true },
      { dataField: "csv_clinic_name", text: "Clinic", csvExport: true, hidden: true },
      { dataField: "csv_streak_length", text: "Streak Length (Months)", csvExport: true, hidden: true },
      { dataField: "csv_status", text: "Status", csvExport: true, hidden: true }
    ];

    return [...uiColumns, ...csvColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    bootstrap4: true,
    page,
    sizePerPage: pageSize,
    totalSize: effectiveTotalRecords,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
    showTotal: effectiveTotalRecords > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 }
    ],
    onPageChange: (nextPage) => {
      setPage(nextPage);
    },
    onSizePerPageChange: (nextSize) => {
      setPageSize(nextSize);
      setPage(1);
    }
  };

  const isRefetching = isReportLoading && !showReportSkeleton;

  return (
    <div className={`cpt-performance-wrapper report-container cpt-dash-report-surface ${isRefetching ? "cpt-dash-report-surface--busy" : ""}`}>
      {isRefetching && (
        <CptDashboardSkeletonOverlay>
          <NonAdherentReportShimmer />
        </CptDashboardSkeletonOverlay>
      )}
      <NonAdherentFilters
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        patientStatusFilter={patientStatusFilter}
        careProviderFilter={careProviderFilter}
        clinicFilter={clinicFilter}
        careProviderOptions={careProviderOptions}
        clinicOptions={clinicOptions}
        onApplyApiFilters={handleFilterApply}
        onReset={handleFilterReset}
        onLocalFilterChange={handleLocalFilterChange}
        loading={isReportLoading}
      />

      {showReportSkeleton ? (
        <NonAdherentReportShimmer />
      ) : !hasLoadedOnce ? (
        <div className="cpt-select-prompt">
          <div className="cpt-select-prompt-icon">
            <i className="mdi mdi-calendar-search"></i>
          </div>
          <h5>Select a month to view the adherence report</h5>
          <p>Choose filters above, then click <strong>Filter</strong> to load adherent/non-adherent patient data.</p>
        </div>
      ) : (
        <>
        <Row className="mb-3">
          <Col md="4">
            <Card className="stat-card-simple na-kpi">
              <CardBody>
                <div className="stat-content">
                  <span className="stat-label">Total Patients</span>
                  <h2 className="stat-number">{totalPatients}</h2>
                </div>
                <div className="stat-icon-wrap blue">
                  <i className="mdi mdi-account-group"></i>
                </div>
              </CardBody>
            </Card>
          </Col>          
          <Col md="4">
            <Card className="stat-card-simple na-kpi">
              <CardBody>
                <div className="stat-content">
                  <span className="stat-label">Adherent Patients</span>
                  <h2 className="stat-number text-success">
                    {adherentPatients} <span className="kpi-percentage">({adherentPercentage}%)</span>
                  </h2>
                </div>
                <div className="stat-icon-wrap green">
                  <i className="mdi mdi-check-circle-outline"></i>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="stat-card-simple na-kpi">
              <CardBody>
                <div className="stat-content">
                  <span className="stat-label">Non-Adherent Patients</span>
                  <h2 className="stat-number text-danger">
                    {nonAdherentPatients}{" "}
                    <span className="kpi-percentage">({summary?.non_adherent_percentage || 0}%)</span>
                  </h2>
                </div>
                <div className="stat-icon-wrap red">
                  <i className="mdi mdi-account-alert-outline"></i>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

      <Card className="details-table-card-modern">
        <CardBody>
            <div className="chart-header-modern">
              <div className="chart-icon-modern"><i className="mdi mdi-account-group-outline"></i></div>
              <div className="chart-header-copy-modern">
                <h4>Patients with Adherent/Non-Adherent Months</h4>
                <p>
                  Review patient-level CPT adherence details across the selected period.
                </p>
              </div>
            </div>

            <ToolkitProvider keyField="patient_id" data={filteredRows} columns={columns} exportCSV={{ fileName: `non-adherent-patients-${selectedYear}-${selectedMonth}.csv` }}>
              {(toolkitProps) => (
                <>
                  <div className="patient-list-toolbar">
                    <div className="patient-list-search-shell">
                      <span className="patient-list-search-icon"><i className="mdi mdi-magnify"></i></span>
                      <input className="patient-list-search-input" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search by name, email, organization, provider, or details" />
                      {searchText && <button type="button" className="patient-list-search-clear" onClick={() => setSearchText("")}><i className="mdi mdi-close-circle"></i></button>}
                    </div>
                    <ExportCSVButton {...toolkitProps.csvProps} className="patient-list-export-btn">
                      <span className="patient-list-export-icon"><i className="mdi mdi-file-excel-outline"></i></span>
                      <span>Export CSV</span>
                    </ExportCSVButton>
                  </div>
                  <BootstrapTable
                    {...toolkitProps.baseProps}
                    bootstrap4
                    bordered={false}
                    wrapperClasses="table-responsive"
                    classes="mpr-patient-table modern-table-enterprise"
                    keyField="_id"
                    defaultSorted={[
                      {
                        dataField: "patientName",
                        order: "asc"
                      }
                    ]}
                    pagination={paginationFactory(paginationOptions)}
                    rowClasses={(row) => ((row?.patient_id || row?._id) === selectedPatientId ? "na-selected-row" : "")}
                    noDataIndication={() => <div className="mpr-empty-row">No non-adherent patients found for the selected filters.</div>}
                  />
                </>
              )}
            </ToolkitProvider>
        </CardBody>
      </Card>

        <div ref={timelineSectionRef}>
          <NonAdherentPatientTimelineReport
            selectedPatientId={selectedPatientId}
            timelineData={timelineData}
            isLoading={isTimelineLoading}
          />
        </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ careprovider }) => ({
  nonAdherentBackToBackReportData: careprovider.nonAdherentBackToBackReportData,
  ongoingNonAdherentBackToBackReportRequest: careprovider.ongoingNonAdherentBackToBackReportRequest,
  nonAdherentBackToBackTimelineData: careprovider.nonAdherentBackToBackTimelineData,
  ongoingNonAdherentBackToBackTimelineRequest: careprovider.ongoingNonAdherentBackToBackTimelineRequest
});

export default connect(mapStateToProps)(NonAdherentBackToBackMonths);
